import { LazoCompanyAccountType } from "@/enums/LazoCompanyAccountType";
import { LazoPaths } from "@/enums/LazoPaths";
import i18n from "@/i18n/config";
import { AddOnModuleType, AddOnObjectKeyType } from "@/models/subscriptions/AddOn";
import { getAddOnMap } from "@/models/subscriptions/AddOnModuleMap";
import { SubscriptionPurchase } from "@/models/subscriptions/SubscriptionPurchase";
import { envIsOneOf } from "@/utils/EnvironmentUtils";
import {
  AccountTree,
  Article,
  AssignmentOutlined,
  BuildOutlined,
  CheckCircle,
  Dashboard,
  Description,
  Group,
  Inventory,
  ListAlt,
  ManageSearch,
} from "@mui/icons-material";
import AccountingLogo from "../../../assets/images/accounting-logo.svg";
import VaultSVG from "../../../assets/images/vault.svg";
import { UsersIcon } from "../utils/icons/UsersIcon";

export const FINANCE_EXTERNAL_HREF = "https://platform.lazo.one/dashboard/finance";

const FINANCE_ALLOWED_COMPANY_NAMES = new Set([
  "artplacer na inc",
  "tonder technologies inc",
  "hotel asset management, llc",
  "ng2 operating services llc",
  "oxtopay inc",
  "quix corporate training llc",
  "flowable ai llc",
  "mappa ia inc",
  "familify corp",
  "croxpay llc",
  "lazo fintech inc",
]);

function isFinanceAllowedCompany(legalName: string | undefined | null): boolean {
  if (!legalName) return false;
  return FINANCE_ALLOWED_COMPANY_NAMES.has(legalName.trim().toLowerCase());
}

export function getItems(
  lazoCompanyAccountType: LazoCompanyAccountType,
  subscriptionPurchase: SubscriptionPurchase,
  isBookkeepingClient: boolean,
  companyLegalName: string | undefined | null
) {
  const items: any[] = [];
  const purchaseAddOnsMap: Record<string, any> = {};

  if (subscriptionPurchase) {
    let addOns = subscriptionPurchase.subscription.addOns;
    if (subscriptionPurchase.extraAddOns) {
      addOns = addOns.concat(subscriptionPurchase.extraAddOns);
    }

    for (let i = 0; i < addOns.length; i++) {
      const _module = addOns[i].module;
      if (!purchaseAddOnsMap[_module]) {
        purchaseAddOnsMap[_module] = [];
      }
      purchaseAddOnsMap[_module].push(addOns[i].objectKey);
    }
  }

  const isIndividual = lazoCompanyAccountType === LazoCompanyAccountType.INDIVIDUAL;
  if (!isIndividual) {
    items.unshift({
      key: "documents",
      label: i18n.t("navigation:drawer.lazoOne.documents"),
      icon: <Description />,
      path: LazoPaths.ONE_DOCUMENT_CENTER,
    });
    items.unshift({
      key: "my_services",
      label: i18n.t("navigation:drawer.lazoOne.myServices"),
      icon: <AssignmentOutlined />,
      path: LazoPaths.MY_SERVICES,
      items: [
        {
          key: "my_tasks",
          label: i18n.t("navigation:drawer.lazoOne.myTasks"),
          icon: <CheckCircle />,
          path: LazoPaths.TASKS,
        },
      ],
    });
  }

  const financeVisible = isBookkeepingClient && isFinanceAllowedCompany(companyLegalName);
  items.push({
    key: "finance",
    label: i18n.t("navigation:drawer.lazoOne.finance"),
    icon: <Dashboard />,
    href: FINANCE_EXTERNAL_HREF,
    hidden: !financeVisible,
  });

  const addOnMap = getAddOnMap();

  switch (lazoCompanyAccountType) {
    case LazoCompanyAccountType.COMPANY: {
      const lazoModule = {
        label: i18n.t("navigation:drawer.sections.accounting"),
        key: "Accounting",
        icon: <img src={AccountingLogo.src} alt="accounting-logo-icon" />,
        items: [],
      };

      lazoModule.items.push(addOnMap[AddOnModuleType.ACCOUNTING][AddOnObjectKeyType.TAX_DUE_DATES]);

      const roomModule = {
        label: i18n.t("navigation:drawer.sections.dataRooms"),
        key: "data-rooms",
        icon: <img src={VaultSVG.src} alt="data-room-icon" />,
        items: [],
      };

      roomModule.items.push(addOnMap[AddOnModuleType.DATA_ROOM][AddOnObjectKeyType.MASTER_DATA_ROOM]);
      roomModule.items.push(addOnMap[AddOnModuleType.VIEW_DATA_SHARED_BY_OTHERS][AddOnObjectKeyType.SHARED_SPACES]);

      items.push(roomModule);

      break;
    }
    case LazoCompanyAccountType.INDIVIDUAL: {
      const roomModule = {
        label: i18n.t("navigation:drawer.sections.dataRooms"),
        key: "data-rooms",
        icon: <img src={VaultSVG.src} alt="data-room-icon" />,
        items: [],
      };

      roomModule.items.push(addOnMap[AddOnModuleType.DATA_ROOM][AddOnObjectKeyType.MASTER_DATA_ROOM]);
      roomModule.items.push(addOnMap[AddOnModuleType.VIEW_DATA_SHARED_BY_OTHERS][AddOnObjectKeyType.SHARED_SPACES]);

      items.push(roomModule);
    }
  }

  return items;
}

export function getPortalItems() {
  const isQA = envIsOneOf(["qa"]);
  const items = [
    {
      key: "customers",
      label: i18n.t("navigation:drawer.portal.customers"),
      icon: <UsersIcon />,
      path: LazoPaths.PORTAL_CUSTOMERS,
    },
    {
      key: "tasks",
      label: i18n.t("navigation:drawer.portal.tasks"),
      icon: <ListAlt />,
      path: LazoPaths.TASKS_ADMIN,
    },
    {
      key: "team",
      label: i18n.t("navigation:drawer.portal.lazoTeam"),
      icon: <Group />,
      path: LazoPaths.LAZO_TEAM,
    },
    {
      key: "product_manager",
      label: i18n.t("navigation:drawer.portal.productManager"),
      icon: <Inventory />,
      path: LazoPaths.PRODUCT_MANAGER,
    },
    {
      key: "document_manager",
      label: i18n.t("navigation:drawer.portal.documentManager"),
      icon: <Article />,
      path: LazoPaths.DOCUMENT_MANAGER,
    },
    {
      key: "search_reporting",
      label: i18n.t("navigation:drawer.portal.searchAndReporting"),
      icon: <ManageSearch />,
      path: LazoPaths.SEARCH_AND_REPORTING,
    },
  ];

  if (isQA) {
    items.push({
      key: "org_chart",
      label: i18n.t("navigation:drawer.portal.orgChart"),
      icon: <AccountTree />,
      path: LazoPaths.ORG_CHART,
    });
  }

  items.push({
    key: "tools",
    label: "Tools",
    icon: <BuildOutlined />,
    path: LazoPaths.TOOLS,
  });

  return items;
}
